import { tick } from "svelte";
import { get, writable, type Writable } from "svelte/store";
import { normalizeAbsolute } from "./path";
import type { Route, RouteNode } from "./route";
import { ScrollHistory } from "./scrollHistroy";

export interface GotoConfig {
  replace?: boolean;
  query?: URLSearchParams;
  hash?: string;
  restoreScroll?: boolean;
  scroll?: {
    target: "anchor" | "restore" | "noscroll";
    behavior?: ScrollBehavior;
  };
}

export interface RouterMode {
  /**
   * Updates the URL in the browser
   * @param path The new path to store in history
   */
  writeURL(path: string, config?: GotoConfig);

  /**
   * Reads the url from the browser
   */
  readURL(): { path: string; config: GotoConfig };

  /**
   * Returns the full url based on the given path
   */
  generateURL(path: string, config?: Omit<GotoConfig, "replace">): string;

  /**
   * Properly destroys mode and its listeners
   */
  destroy();
}

export interface ScrollHistoryMode {
  store(path: string);
  apply(path: string, restore: boolean, behavior?: ScrollBehavior);
}

export class Router implements RouteNode {
  private mode: RouterMode;
  private scrollHistory: ScrollHistoryMode = new ScrollHistory();
  private store: Writable<{ breadcrumbs: any[]; matched: Route[] }> = writable({
    breadcrumbs: [],
    matched: [],
  });
  readonly currentPath: Writable<string> = writable("");

  goto(p: string, config: GotoConfig = {}) {
    this.scrollHistory.store(get(this.currentPath));

    const path = normalizeAbsolute(p);
    // reset breadcrumbs and matched child because path will change
    this.store.set({ breadcrumbs: [], matched: [] });
    this.mode.writeURL(path, config); // write url to browser history
    this.currentPath.set(path); // inform children

    if (config.restoreScroll || config.scroll?.target === "restore") {
      this.scrollHistory.apply(p, true, config.scroll?.behavior);
    } else if (config.scroll?.target === "anchor") {
      tick().then(() => {
        const anchor = config.hash ? document.getElementById(config.hash) : null;
        anchor?.scrollIntoView({ behavior: config.scroll.behavior });
      });
    } else if (config.scroll?.target !== "noscroll") {
      this.applyScroll({ top: 0, left: 0 });
    }
  }

  generatePath(path: string) {
    return normalizeAbsolute(path);
  }

  generateURL(path: string = "", config?: Omit<GotoConfig, "replace">) {
    return this.mode.generateURL(normalizeAbsolute(path), config);
  }

  matches = (children: Route[]) => {
    const breadcrumbs = children.map((child) => child.breadcrumb).filter((i) => i);
    this.store.set({ breadcrumbs, matched: children });
  };
  hasMatch() {
    return get(this.store).matched.length > 0;
  }

  setMode(mode: RouterMode) {
    // properly remove old mode
    if (this.mode) {
      this.mode.destroy();
    }

    this.mode = mode;
  }

  async applyScroll(pos: { top: number; left: number }) {
    await tick();
    window.scrollTo(pos);
  }

  get subscribe() {
    return this.store.subscribe;
  }
}

export const router = new Router();
